import React from 'react';
import Icon from '../../../components/AppIcon';

const TransactionDetails = ({ transaction }) => {
  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      card: 'Credit/Debit Card',
      upi: 'UPI',
      netbanking: 'Net Banking',
      wallet: 'Digital Wallet'
    };
    return methods?.[method] || method;
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      card: 'CreditCard',
      upi: 'Smartphone',
      netbanking: 'Building2',
      wallet: 'Wallet'
    };
    return icons?.[method] || 'CreditCard';
  };

  const subtotal = transaction?.amount || 0;
  const discount = transaction?.discount || 0;
  const tax = Math.round(subtotal * 0.18);
  const total = subtotal - discount + tax;

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-warm-sm">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Receipt" size={20} className="text-primary" />
        <h2 className="text-lg font-heading font-bold text-foreground">
          Transaction Details
        </h2>
      </div>
      <div className="space-y-4">
        {/* Transaction ID */}
        <div className="flex justify-between items-start pb-4 border-b border-border">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Transaction ID</p>
            <p className="font-mono text-sm font-medium text-foreground">{transaction?.id}</p>
          </div>
          <div className="bg-success/10 text-success px-3 py-1 rounded-full text-xs font-semibold">
            Completed
          </div>
        </div>

        {/* Date & Time */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Date & Time</span>
          <span className="text-sm font-medium text-foreground">
            {formatDate(transaction?.timestamp)}
          </span>
        </div>

        {/* Payment Method */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Payment Method</span>
          <div className="flex items-center gap-2">
            <Icon name={getPaymentMethodIcon(transaction?.paymentMethod)} size={16} className="text-foreground" />
            <span className="text-sm font-medium text-foreground">
              {getPaymentMethodLabel(transaction?.paymentMethod)}
            </span>
          </div>
        </div>

        {/* Billing Breakdown */}
        <div className="pt-4 border-t border-border space-y-3">
          <p className="text-sm font-semibold text-foreground mb-3">Billing Breakdown</p>
          
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-foreground">{transaction?.plan?.name} Plan</p>
              <p className="text-xs text-muted-foreground">
                {transaction?.billingCycle === 'monthly' ? 'Monthly' : 'Annual'} subscription
              </p>
            </div>
            <span className="text-sm font-medium text-foreground">${subtotal}</span>
          </div>

          {discount > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-success">Discount Applied</span>
              <span className="text-sm font-medium text-success">-${discount}</span>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Tax (GST 18%)</span>
            <span className="text-sm font-medium text-foreground">${tax}</span>
          </div>

          <div className="flex justify-between items-center pt-3 border-t border-border">
            <span className="text-base font-heading font-bold text-foreground">Total Paid</span>
            <span className="text-xl font-heading font-bold text-primary">${total}</span>
          </div>
        </div>

        {/* Next Billing Date */}
        <div className="bg-accent/10 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-2">
            <Icon name="Calendar" size={16} className="text-accent mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Next Billing Date</p>
              <p className="text-sm text-muted-foreground">
                {transaction?.billingCycle === 'monthly' ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;